import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  Briefcase, Plus, Users, MapPin, Clock, Calendar, Eye, 
  Loader2, UserPlus, Star, ChevronRight, X
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { PhoneInput } from '../components/ui/phone-input';

const Recruitment = () => {
  const [summary, setSummary] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [jobTitles, setJobTitles] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [showJobModal, setShowJobModal] = useState(false);
  const [showCandidateModal, setShowCandidateModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  
  const [jobForm, setJobForm] = useState({
    title: '',
    description: '',
    requirements: '',
    job_type: 'full_time',
    location: '',
    positions_available: 1,
    closing_date: ''
  });
  
  const [candidateForm, setCandidateForm] = useState({
    job_id: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    cover_letter: ''
  });
  
  const { getAuthHeaders, currentOffice } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) {
        headers['X-Office-ID'] = currentOffice.id;
      }

      const [summaryRes, jobsRes, candidatesRes, jobTitlesRes] = await Promise.all([
        fetch(`${API_URL}/api/recruitment/summary`, { headers }),
        fetch(`${API_URL}/api/recruitment/jobs`, { headers }),
        fetch(`${API_URL}/api/recruitment/candidates`, { headers }),
        fetch(`${API_URL}/api/system-config/job-titles`, { headers })
      ]);

      if (summaryRes.ok) setSummary(await summaryRes.json());
      if (jobsRes.ok) setJobs(await jobsRes.json());
      if (candidatesRes.ok) setCandidates(await candidatesRes.json());
      if (jobTitlesRes.ok) setJobTitles(await jobTitlesRes.json());
    } catch (error) {
      console.error('Error fetching recruitment data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateJob = async () => {
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) {
        headers['X-Office-ID'] = currentOffice.id;
      }
      
      const response = await fetch(`${API_URL}/api/recruitment/jobs`, {
        method: 'POST',
        headers,
        body: JSON.stringify(jobForm)
      });
      
      if (response.ok) {
        setShowJobModal(false);
        setJobForm({ title: '', description: '', requirements: '', job_type: 'full_time', location: '', positions_available: 1, closing_date: '' });
        fetchData();
      }
    } catch (error) {
      console.error('Error creating job:', error);
    }
  };

  const handleAddCandidate = async () => {
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) {
        headers['X-Office-ID'] = currentOffice.id;
      }
      
      const response = await fetch(`${API_URL}/api/recruitment/candidates`, {
        method: 'POST',
        headers,
        body: JSON.stringify(candidateForm)
      });
      
      if (response.ok) {
        setShowCandidateModal(false);
        setCandidateForm({ job_id: '', first_name: '', last_name: '', email: '', phone: '', cover_letter: '' });
        fetchData();
      }
    } catch (error) {
      console.error('Error adding candidate:', error);
    }
  };

  const handleUpdateCandidateStatus = async (candidateId, newStatus) => {
    try {
      const headers = getAuthHeaders();
      await fetch(`${API_URL}/api/recruitment/candidates/${candidateId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ status: newStatus })
      });
      fetchData();
    } catch (error) {
      console.error('Error updating candidate:', error);
    }
  };

  const handleUpdateJobStatus = async (jobId, newStatus) => {
    try {
      const headers = getAuthHeaders();
      await fetch(`${API_URL}/api/recruitment/jobs/${jobId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ status: newStatus })
      });
      fetchData();
    } catch (error) {
      console.error('Error updating job:', error);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800',
      on_hold: 'bg-yellow-100 text-yellow-800',
      applied: 'bg-blue-100 text-blue-800',
      screening: 'bg-purple-100 text-purple-800',
      interview: 'bg-orange-100 text-orange-800',
      offer: 'bg-green-100 text-green-800',
      hired: 'bg-green-600 text-white',
      rejected: 'bg-red-100 text-red-800'
    };
    return <Badge className={styles[status] || 'bg-gray-100'}>{status?.replace('_', ' ')}</Badge>;
  };

  const getJobTypeBadge = (type) => {
    const labels = {
      full_time: 'Full-time',
      part_time: 'Part-time',
      contract: 'Contract',
      internship: 'Internship'
    };
    return <Badge variant="outline">{labels[type] || type}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="recruitment-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Recruitment</h1>
          <p className="text-gray-600 dark:text-gray-400">Manage job postings and candidates</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowCandidateModal(true)}>
            <UserPlus className="h-4 w-4 mr-2" />
            Add Candidate
          </Button>
          <Button onClick={() => setShowJobModal(true)} data-testid="post-job-button">
            <Plus className="h-4 w-4 mr-2" />
            Post Job
          </Button>
        </div>
      </div>

      {/* Stats */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Open Positions</p>
                  <p className="text-2xl font-bold text-blue-600">{summary.open_positions}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Briefcase className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Applicants</p>
                  <p className="text-2xl font-bold">{summary.total_applicants}</p>
                </div>
                <div className="p-3 bg-green-100 rounded-lg">
                  <Users className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">In Interview</p>
                  <p className="text-2xl font-bold text-orange-600">{summary.in_interview}</p>
                </div>
                <div className="p-3 bg-orange-100 rounded-lg">
                  <Calendar className="h-6 w-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Hired This Month</p>
                  <p className="text-2xl font-bold text-purple-600">{summary.hired_this_month}</p>
                </div>
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Star className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Job Postings */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Job Postings</CardTitle>
              <Badge variant="outline">{jobs.length} jobs</Badge>
            </div>
          </CardHeader>
          <CardContent>
            {jobs.length > 0 ? (
              <div className="space-y-4">
                {jobs.map((job) => (
                  <div key={job.id} className="p-4 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h3 className="font-semibold text-lg">{job.title}</h3>
                        <p className="text-sm text-gray-500">{job.department}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {getJobTypeBadge(job.job_type)}
                        {getStatusBadge(job.status)}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                      {job.location && (
                        <span className="flex items-center gap-1">
                          <MapPin className="h-4 w-4" />
                          {job.location}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        {job.applicant_count} applicants
                      </span>
                      {job.closing_date && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          Closes {new Date(job.closing_date).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => { setSelectedJob(job); setCandidateForm({...candidateForm, job_id: job.id}); setShowCandidateModal(true); }}
                      >
                        <UserPlus className="h-4 w-4 mr-1" />
                        Add Candidate
                      </Button>
                      {job.status === 'open' && (
                        <Button variant="outline" size="sm" onClick={() => handleUpdateJobStatus(job.id, 'closed')}>
                          Close
                        </Button>
                      )}
                      {job.status === 'closed' && (
                        <Button variant="outline" size="sm" onClick={() => handleUpdateJobStatus(job.id, 'open')}>
                          Reopen
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Briefcase className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No job postings yet</p>
                <Button variant="outline" className="mt-4" onClick={() => setShowJobModal(true)}>
                  Post First Job
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Candidates Pipeline */}
        <Card>
          <CardHeader>
            <CardTitle>Candidate Pipeline</CardTitle>
          </CardHeader>
          <CardContent>
            {candidates.length > 0 ? (
              <div className="space-y-3">
                {candidates.slice(0, 10).map((candidate) => (
                  <div key={candidate.id} className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h4 className="font-medium">{candidate.first_name} {candidate.last_name}</h4>
                        <p className="text-xs text-gray-500">{candidate.job_title}</p>
                      </div>
                      {getStatusBadge(candidate.status)}
                    </div>
                    <div className="flex gap-1 flex-wrap">
                      {candidate.status === 'applied' && (
                        <Button size="sm" variant="ghost" onClick={() => handleUpdateCandidateStatus(candidate.id, 'screening')}>
                          Screen
                        </Button>
                      )}
                      {candidate.status === 'screening' && (
                        <Button size="sm" variant="ghost" onClick={() => handleUpdateCandidateStatus(candidate.id, 'interview')}>
                          Interview
                        </Button>
                      )}
                      {candidate.status === 'interview' && (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => handleUpdateCandidateStatus(candidate.id, 'offer')}>
                            Offer
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleUpdateCandidateStatus(candidate.id, 'rejected')}>
                            Reject
                          </Button>
                        </>
                      )}
                      {candidate.status === 'offer' && (
                        <Button size="sm" variant="ghost" className="text-green-600" onClick={() => handleUpdateCandidateStatus(candidate.id, 'hired')}>
                          Hire
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No candidates yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Job Modal */}
      <Dialog open={showJobModal} onOpenChange={setShowJobModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Post New Job</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Job Title *</Label>
                <Select value={jobForm.title} onValueChange={(v) => setJobForm({...jobForm, title: v})}>
                  <SelectTrigger><SelectValue placeholder="Select job title" /></SelectTrigger>
                  <SelectContent>
                    {jobTitles.map((jt) => (
                      <SelectItem key={jt.id} value={jt.title}>{jt.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Location</Label>
                <Input value={jobForm.location} onChange={(e) => setJobForm({...jobForm, location: e.target.value})} placeholder="e.g., Dar es Salaam" />
              </div>
              <div>
                <Label>Job Type</Label>
                <Select value={jobForm.job_type} onValueChange={(v) => setJobForm({...jobForm, job_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full_time">Full-time</SelectItem>
                    <SelectItem value="part_time">Part-time</SelectItem>
                    <SelectItem value="contract">Contract</SelectItem>
                    <SelectItem value="internship">Internship</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Positions</Label>
                <Input type="number" value={jobForm.positions_available} onChange={(e) => setJobForm({...jobForm, positions_available: parseInt(e.target.value) || 1})} min="1" />
              </div>
              <div>
                <Label>Closing Date</Label>
                <Input type="date" value={jobForm.closing_date} onChange={(e) => setJobForm({...jobForm, closing_date: e.target.value})} />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={jobForm.description} onChange={(e) => setJobForm({...jobForm, description: e.target.value})} placeholder="Job description..." rows={3} />
            </div>
            <div>
              <Label>Requirements</Label>
              <Textarea value={jobForm.requirements} onChange={(e) => setJobForm({...jobForm, requirements: e.target.value})} placeholder="Skills and qualifications..." rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowJobModal(false)}>Cancel</Button>
            <Button onClick={handleCreateJob} disabled={!jobForm.title}>Post Job</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Candidate Modal */}
      <Dialog open={showCandidateModal} onOpenChange={setShowCandidateModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Candidate</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Job Position *</Label>
              <Select value={candidateForm.job_id} onValueChange={(v) => setCandidateForm({...candidateForm, job_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select job" /></SelectTrigger>
                <SelectContent>
                  {jobs.filter(j => j.status === 'open').map((job) => (
                    <SelectItem key={job.id} value={job.id}>{job.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>First Name *</Label>
                <Input value={candidateForm.first_name} onChange={(e) => setCandidateForm({...candidateForm, first_name: e.target.value})} />
              </div>
              <div>
                <Label>Last Name *</Label>
                <Input value={candidateForm.last_name} onChange={(e) => setCandidateForm({...candidateForm, last_name: e.target.value})} />
              </div>
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" value={candidateForm.email} onChange={(e) => setCandidateForm({...candidateForm, email: e.target.value})} />
            </div>
            <div>
              <Label>Phone</Label>
              <PhoneInput 
                value={candidateForm.phone} 
                onChange={(value) => setCandidateForm({...candidateForm, phone: value || ''})} 
                data-testid="candidate-phone-input"
              />
            </div>
            <div>
              <Label>Cover Letter</Label>
              <Textarea value={candidateForm.cover_letter} onChange={(e) => setCandidateForm({...candidateForm, cover_letter: e.target.value})} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCandidateModal(false)}>Cancel</Button>
            <Button onClick={handleAddCandidate} disabled={!candidateForm.job_id || !candidateForm.first_name || !candidateForm.email}>
              Add Candidate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Recruitment;
